// Behavior of the server-rendered Devise pages (app/views/users/): show/hide password,
// client-side validation, the Microsoft SSO step on sign in and reCAPTCHA on sign up.
// The forms work without it: Devise validates again on the server.

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Shows (or clears) the message under a field and marks the input for assistive tech. */
export function setFieldError(input: HTMLInputElement, message: string): void {
  const field = input.closest('.field')
  const slot = field?.querySelector<HTMLElement>('.field-err')
  if (message) input.setAttribute('aria-invalid', 'true')
  else input.removeAttribute('aria-invalid')
  if (!slot) return
  slot.textContent = message
  slot.hidden = !message
  if (message) {
    slot.id ||= `${input.id}-err`
    input.setAttribute('aria-describedby', slot.id)
  } else {
    input.removeAttribute('aria-describedby')
  }
}

/** Every `.pw-toggle` switches its input between hidden and visible text. */
export function wirePasswordToggles(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>('.pw-toggle').forEach((btn) => {
    const input = btn.parentElement?.querySelector<HTMLInputElement>('input')
    if (!input) return
    btn.addEventListener('click', () => {
      const show = input.type === 'password'
      input.type = show ? 'text' : 'password'
      // The name stays «Mostrar contraseña»; aria-pressed carries the state (no double negative).
      btn.setAttribute('aria-pressed', String(show))
    })
  })
}

/** Full-page POST to the OmniAuth request phase (CSRF-protected): the browser follows the redirects. */
function postToSso(action: string, csrf: string, email: string): void {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = action
  for (const [name, value] of [['authenticity_token', csrf], ['email', email]]) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

function wireSignIn(form: HTMLFormElement): void {
  const email = form.querySelector<HTMLInputElement>('#user_email')
  const password = form.querySelector<HTMLInputElement>('#user_password')
  const passwordView = form.querySelector<HTMLElement>('#signin-password')
  const ssoStep = form.querySelector<HTMLElement>('#sso-step')
  const ssoEmail = form.querySelector<HTMLInputElement>('#sso-email')
  const ssoMsg = form.querySelector<HTMLElement>('#sso-msg')
  const ssoContinue = form.querySelector<HTMLButtonElement>('#sso-continue')
  if (!email || !password || !passwordView || !ssoStep || !ssoEmail || !ssoMsg || !ssoContinue) return

  form.addEventListener('submit', (e) => {
    if (!ssoStep.hidden) { e.preventDefault(); return } // Enter in the SSO step never posts the password form.
    const badEmail = !EMAIL.test(email.value.trim())
    const noPassword = !password.value
    setFieldError(email, badEmail ? 'Ingresá un email válido' : '')
    setFieldError(password, noPassword ? 'Ingresá tu contraseña' : '')
    if (badEmail || noPassword) {
      e.preventDefault()
      ;(badEmail ? email : password).focus()
    }
  })

  const showSso = (open: boolean) => {
    passwordView.hidden = open
    ssoStep.hidden = !open
    if (open) {
      if (!ssoEmail.value) ssoEmail.value = email.value
      ssoEmail.focus()
    } else {
      email.focus()
    }
  }
  form.querySelector('#sso-open')?.addEventListener('click', () => showSso(true))
  form.querySelector('#sso-back')?.addEventListener('click', (e) => { e.preventDefault(); showSso(false) })

  const sayInStep = (message: string) => {
    ssoMsg.textContent = message
    ssoMsg.hidden = !message
  }
  const route = async () => {
    const value = ssoEmail.value.trim()
    if (!EMAIL.test(value)) { sayInStep(''); setFieldError(ssoEmail, 'Ingresá un email válido'); ssoEmail.focus(); return }
    setFieldError(ssoEmail, '')
    sayInStep('')
    ssoContinue.disabled = true
    try {
      const res = await fetch(form.dataset.ssoRoute || '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': form.dataset.csrf || '' },
        body: JSON.stringify({ email: value }),
      })
      if (!res.ok) throw new Error(`sso route ${res.status}`)
      const data = (await res.json()) as { sso?: boolean }
      if (data.sso) { postToSso(form.dataset.ssoAuth || '', form.dataset.csrf || '', value); return }
      // Asked for SSO and the account has none: say so; falling back silently would hide the answer.
      sayInStep('El ingreso con Microsoft no está configurado para esta cuenta. Ingresá con tu contraseña.')
    } catch {
      sayInStep('No pudimos verificarlo. Intentá de nuevo.')
    }
    ssoContinue.disabled = false
  }
  ssoContinue.addEventListener('click', () => { void route() })
  ssoEmail.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void route() } })
}

interface Grecaptcha {
  ready?: (cb: () => void) => void
  execute?: (siteKey: string, opts: { action: string }) => Promise<string>
  render?: (el: HTMLElement, opts: { sitekey: string }) => number
}
// Read through a local type: Google's script attaches it to window at runtime.
const grecaptcha = (): Grecaptcha | undefined => (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha

function loadRecaptcha(version: string, siteKey: string): void {
  if (document.getElementById('recaptcha-script')) return
  const script = document.createElement('script')
  script.id = 'recaptcha-script'
  script.async = true
  script.src = version === 'v3'
    ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    : 'https://www.google.com/recaptcha/api.js'
  document.head.appendChild(script)
}

/** Resolves once grecaptcha is loaded and ready (the script loads async). */
function recaptchaReady(): Promise<Grecaptcha> {
  return new Promise((resolve) => {
    const poll = () => {
      const g = grecaptcha()
      if (g?.ready) g.ready(() => resolve(g))
      else window.setTimeout(poll, 100)
    }
    poll()
  })
}

/** Shows a form-level message in the card's error box (used for reCAPTCHA failures). */
function showFormError(form: HTMLFormElement, message: string): void {
  const box = form.closest('.auth-card')?.querySelector<HTMLElement>('.auth-errors')
  if (!box) return
  box.innerHTML = ''
  const list = document.createElement('ul')
  const item = document.createElement('li')
  item.textContent = message
  list.appendChild(item)
  box.appendChild(list)
  box.hidden = false
}

function wireSignUp(form: HTMLFormElement): void {
  const get = (id: string) => form.querySelector<HTMLInputElement>(`#${id}`)
  const nombre = get('user_nombre'); const email = get('user_email'); const phone = get('user_telefono')
  const password = get('user_password'); const confirm = get('user_password_confirmation'); const terms = get('accept-terms')
  if (!nombre || !email || !phone || !password || !confirm || !terms) return
  const minLength = Number(form.dataset.minPassword) || 8
  const version = form.dataset.recaptchaVersion
  const siteKey = form.dataset.recaptchaSiteKey || ''
  const action = form.dataset.recaptchaAction || 'sign_up'
  const submit = form.querySelector<HTMLButtonElement>('button[type=submit]')

  if (version && siteKey) {
    loadRecaptcha(version, siteKey)
    const box = form.querySelector<HTMLElement>('#recaptcha-v2')
    if (version === 'v2' && box) void recaptchaReady().then((g) => { g.render?.(box, { sitekey: siteKey }) })
  }

  // Same rules and copy as the previous sign-up page; Devise validates again on the server.
  const validate = (): HTMLInputElement | null => {
    const name = nombre.value.trim(); const mail = email.value.trim(); const tel = phone.value.trim()
    const checks: [HTMLInputElement, string][] = [
      [nombre, !name ? 'El nombre es requerido' : name.length < 3 ? 'El nombre debe tener al menos 3 caracteres' : ''],
      [email, !mail ? 'El email es requerido' : !EMAIL.test(mail) ? 'Ingresá un email válido' : ''],
      [phone, tel && tel.length < 8 ? 'El teléfono debe tener al menos 8 dígitos' : ''],
      [password, !password.value ? 'La contraseña es requerida' : password.value.length < minLength ? `La contraseña debe tener al menos ${minLength} caracteres` : ''],
      [confirm, !confirm.value ? 'Confirmá tu contraseña' : confirm.value !== password.value ? 'Las contraseñas no coinciden' : ''],
      [terms, terms.checked ? '' : 'Debés aceptar los términos y condiciones'],
    ]
    checks.forEach(([input, message]) => setFieldError(input, message))
    return checks.find(([, message]) => message)?.[0] ?? null
  }

  let submitting = false
  form.addEventListener('submit', (e) => {
    if (submitting) return
    const firstBad = validate()
    if (firstBad) { e.preventDefault(); firstBad.focus(); return }
    if (version !== 'v3' || !siteKey) return
    // v3: fetch a token first, then submit for real.
    e.preventDefault()
    if (submit) submit.disabled = true
    recaptchaReady()
      .then((g) => g.execute?.(siteKey, { action }) ?? Promise.reject(new Error('no execute')))
      .then((token) => {
        const field = form.querySelector<HTMLInputElement>('#recaptcha-token')
        if (field) field.value = token
        submitting = true
        form.submit()
      })
      .catch(() => {
        if (submit) submit.disabled = false
        showFormError(form, 'No se pudo validar reCAPTCHA. Intentá de nuevo.')
      })
  })
}

function wirePasswordNew(form: HTMLFormElement): void {
  const email = form.querySelector<HTMLInputElement>('#user_email')
  if (!email) return
  form.addEventListener('submit', (e) => {
    const mail = email.value.trim()
    const message = !mail ? 'El email es requerido' : !EMAIL.test(mail) ? 'Ingresá un email válido' : ''
    setFieldError(email, message)
    if (message) { e.preventDefault(); email.focus() }
  })
}

function wirePasswordEdit(form: HTMLFormElement): void {
  const password = form.querySelector<HTMLInputElement>('#user_password')
  const confirm = form.querySelector<HTMLInputElement>('#user_password_confirmation')
  if (!password || !confirm) return
  const minLength = Number(form.dataset.minPassword) || 8
  form.addEventListener('submit', (e) => {
    const checks: [HTMLInputElement, string][] = [
      [password, !password.value ? 'La contraseña es requerida' : password.value.length < minLength ? `La contraseña debe tener al menos ${minLength} caracteres` : ''],
      [confirm, !confirm.value ? 'Confirmá tu contraseña' : confirm.value !== password.value ? 'Las contraseñas no coinciden' : ''],
    ]
    checks.forEach(([input, message]) => setFieldError(input, message))
    const firstBad = checks.find(([, message]) => message)?.[0]
    if (firstBad) { e.preventDefault(); firstBad.focus() }
  })
}

/** Entry point, called from marketing.ts on every page: a no-op unless an auth form is present. */
export function wireAuth(): void {
  const form = document.querySelector<HTMLFormElement>('form[data-auth]')
  if (!form) return
  wirePasswordToggles(form)
  if (form.dataset.auth === 'signin') wireSignIn(form)
  if (form.dataset.auth === 'signup') wireSignUp(form)
  if (form.dataset.auth === 'password-new') wirePasswordNew(form)
  if (form.dataset.auth === 'password-edit') wirePasswordEdit(form)
}
